/**
 * Confirm Delete User Modal
 * Warning modal with confirmation for permanently deleting a user
 */

import { useState } from 'react';
import { useDeleteUser, useDeactivateUser } from '@/hooks/useUsers';
import { toast } from 'sonner';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface ConfirmDeleteUserModalProps {
  user: {
    id: string;
    displayName: string;
    email: string;
    isActive: boolean;
  };
  onClose: () => void;
}

const ConfirmDeleteUserModal = ({ user, onClose }: ConfirmDeleteUserModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const deleteUserMutation = useDeleteUser();
  const deactivateUserMutation = useDeactivateUser();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // If user is active, deactivate first (backend requires it)
      if (user.isActive) {
        await deactivateUserMutation.mutateAsync(user.id);
      }

      await deleteUserMutation.mutateAsync(user.id);
      toast.success(`Потребител "${user.displayName}" беше изтрит успешно.`);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Грешка при изтриване на потребител';
      toast.error('Неуспешно изтриване', { description: message, duration: 8000 });
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-t-lg">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 id={titleId} className="text-lg font-semibold text-red-800 dark:text-red-300">
            Изтриване на потребител
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
              {user.displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{user.displayName}</div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">{user.email}</div>
            </div>
          </div>

          {user.isActive && (
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Потребителят е активен и ще бъде деактивиран преди изтриване.
              </p>
            </div>
          )}

          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
              Това действие е необратимо!
            </p>
            <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
              <li>Всички съобщения ще загубят автора си</li>
              <li>Членствата в канали ще бъдат премахнати</li>
              <li>Всички настройки и данни ще бъдат изтрити</li>
            </ul>
          </div>

          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
            Сигурни ли сте, че искате да изтриете <span className="font-semibold text-gray-900 dark:text-dark-text-primary">{user.displayName}</span> завинаги?
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-primary rounded-lg transition-colors disabled:opacity-50"
          >
            Откажи
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {isDeleting ? 'Изтриване...' : 'Изтрий завинаги'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteUserModal;
